import { Flex } from "@chakra-ui/layout";
import { Skeleton } from "@chakra-ui/react";
import _ from "lodash";
import { useCallback, useMemo } from "react";
import { GenericError } from "src/components/Feedback/GenericError";
import { FormikSwitchableContent } from "src/components/Inputs/FormikSwitchableContent";
import { FormikSwitchInput } from "src/components/Inputs/FormikSwitchInput";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import * as OrgService from "src/services/orgService";
import { isNotNull, isNullOrUndefined } from "src/services/predicates";
import * as RD from "src/types/remoteData";
import { z } from "zod";

type Props = {
  isSsoOrgRemoteData: RD.RemoteData<unknown, boolean>;
};
export const Form: React.FC<Props> = ({ isSsoOrgRemoteData }) => {
  return (
    <RemoteDataView
      error={(error) => <GenericError error={error} />}
      remoteData={isSsoOrgRemoteData}
      loading={<Loading />}
      reloading={null}
    >
      {(isSsoOrg) => {
        if (isSsoOrg) {
          return null;
        }

        return (
          <FormikSwitchableContent<"enabled" | "disabled">
            label="Require multiple factor authentication"
            description="Everyone in the organization will be prompted to enroll an additional factor before they log in again."
            name="mfa.status"
            checked={["enabled", ""]}
            unchecked={["disabled", ""]}
            showContent={(value) => value === "enabled"}
          >
            <FormikSwitchInput<boolean>
              name="mfa.otp"
              checked={[true, ""]}
              unchecked={[false, ""]}
              variant="checkbox"
              label="Authenticator app"
              description="Staff can enter codes from a mobile Authenticator app like Google Authenticator"
              direction="row"
            />
            <FormikSwitchInput<boolean>
              name="mfa.phone"
              checked={[true, ""]}
              unchecked={[false, ""]}
              variant="checkbox"
              label="SMS"
              description="Staff can enter codes received via SMS"
              direction="row"
            />
          </FormikSwitchableContent>
        );
      }}
    </RemoteDataView>
  );
};

const Loading = () => {
  return (
    <Flex direction="column" gap="4">
      <Skeleton height="3rem" maxWidth="20rem" />
      <Skeleton marginLeft="6" height="3rem" maxWidth="20rem" />
      <Skeleton marginLeft="6" height="3rem" maxWidth="20rem" />
    </Flex>
  );
};

export function useForm(): {
  initialValues: RD.RemoteData<Error, FormikType>;
  submit: (values: FormikType) => Promise<void>;
} {
  const { remoteData, update } =
    OrgService.useOrgConfig<OrgConfig.MfaConfig>("Mfa");
  const initialValues = useMemo(() => {
    return remoteData.map((maybeConfig): FormikType => {
      const config = isNullOrUndefined(maybeConfig)
        ? OrgConfig.MfaConfigSchema.default
        : maybeConfig;

      return {
        mfa: {
          status: config.disabled === false ? "enabled" : "disabled",
          otp: isNotNull(
            config.enabledFactors.find((factor) => factor === "otp")
          ),
          phone: isNotNull(
            config.enabledFactors.find((factor) => factor === "phone")
          ),
        },
      };
    });
  }, [remoteData]);

  const submit = useCallback(
    async (values: FormikType) => {
      if (_.isEqual(initialValues.toNullable(), values)) {
        return;
      }

      if (values.mfa.status === "enabled") {
        const enabledFactors: OrgConfig.MfaFactor[] = [];
        if (values.mfa.otp) enabledFactors.push("otp");
        if (values.mfa.phone) enabledFactors.push("phone");
        await update({
          disabled: false,
          enabledFactors,
        });
      } else {
        await update({
          disabled: true,
          enabledFactors: remoteData.toNullable()?.enabledFactors ?? [], // keep enabled factors as is
        });
      }
    },
    [initialValues, remoteData, update]
  );

  return {
    initialValues,
    submit,
  };
}

export const FormikSchema = z.object({
  mfa: z
    .discriminatedUnion("status", [
      z.object({
        status: z.literal("enabled"),
        otp: z.boolean(),
        phone: z.boolean(),
      }),
      z.object({
        status: z.literal("disabled"),
        otp: z.boolean(),
        phone: z.boolean(),
      }),
    ])
    .superRefine((value, ctx) => {
      if (value.status === "disabled") {
        return;
      }

      if (!value.otp && !value.phone) {
        ctx.addIssue({
          code: "custom",
          message: "Select at least one authentication factor",
          path: ["status"],
        });
      }
    }),
});

type FormikType = z.infer<typeof FormikSchema>;
